'use client';

import React, { useState, useEffect, useCallback } from 'react';
import Icon from '@/components/ui/AppIcon';
import { XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';


interface SourceStatus {
  id: string;
  name: string;
  icon: string;
  uptime: number;
  latency: number;
  status: 'healthy' | 'degraded' | 'error';
  lastSync: string;
  records: number;
  color: string;
}

interface PipelineRun {
  source: string;
  type: string;
  status: string;
  records: number;
  duration: string;
  anomalies: number;
  time: string;
}

interface QualityEntry {
  source: string;
  completeness: number;
  accuracy: number;
  freshness: number;
  consistency: number;
}

const STATIC_SOURCES: SourceStatus[] = [
  { id: 'nhl_official', name: 'NHL Official API', icon: '🏒', uptime: 99.9, latency: 89, status: 'healthy', lastSync: '—', records: 0, color: '#00D4FF' },
  { id: 'api_hockey', name: 'API-Hockey', icon: '📊', uptime: 99.7, latency: 145, status: 'healthy', lastSync: '—', records: 0, color: '#00C853' },
  { id: 'odds_api', name: 'The Odds API', icon: '📈', uptime: 98.9, latency: 210, status: 'healthy', lastSync: '—', records: 0, color: '#FFB800' },
  { id: 'injury_feed', name: 'Injury Feed', icon: '🏥', uptime: 97.8, latency: 320, status: 'healthy', lastSync: '—', records: 0, color: '#FF6B35' },
  { id: 'goalie_feed', name: 'Goalie Confirmations', icon: '🥅', uptime: 99.2, latency: 178, status: 'healthy', lastSync: '—', records: 0, color: '#A855F7' },
];

const QUALITY_DATA: QualityEntry[] = [
  { source: 'NHL Official', completeness: 99.1, accuracy: 98.7, freshness: 99.9, consistency: 98.4 },
  { source: 'API-Hockey', completeness: 97.8, accuracy: 96.9, freshness: 98.2, consistency: 97.1 },
  { source: 'Odds API', completeness: 96.4, accuracy: 95.8, freshness: 97.1, consistency: 96.2 },
  { source: 'Injury Feed', completeness: 94.2, accuracy: 93.7, freshness: 95.8, consistency: 94.1 },
  { source: 'Goalie Feed', completeness: 98.9, accuracy: 98.4, freshness: 99.2, consistency: 98.7 },
];

const TOOLTIP_STYLE = { backgroundColor: '#111827', border: '1px solid #1E2D4A', borderRadius: '6px', color: '#E8EDF5', fontSize: '11px' };

function timeSince(ts: number): string {
  const diff = Math.floor((Date.now() - ts) / 1000);
  if (diff < 60) return `${diff}s`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  return `${Math.floor(diff / 3600)}h`;
}

export default function PipelineClient() {
  const [activeSource, setActiveSource] = useState('api_hockey');
  const [refreshing, setRefreshing] = useState(false);
  const [sources, setSources] = useState<SourceStatus[]>(STATIC_SOURCES);
  const [recentRuns, setRecentRuns] = useState<PipelineRun[]>([]);
  const [historyData, setHistoryData] = useState<{ time: string; records: number; errors: number; latency: number }[]>([]);
  const [apiStatus, setApiStatus] = useState<{ plan?: string; requestsUsed?: number; requestsLimit?: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<number>(Date.now());

  const fetchLiveData = useCallback(async () => {
    const startTime = Date.now();
    try {
      setError(null);

      // Fetch today's games from API-Hockey
      const [todayRes, liveRes, statusRes] = await Promise.allSettled([
        fetch('/api/hockey?action=today'),
        fetch('/api/hockey?action=live'),
        fetch('/api/hockey?action=status'),
      ]);

      let todayGames: unknown[] = [];
      let liveGames: unknown[] = [];
      let latency = Date.now() - startTime;

      if (todayRes.status === 'fulfilled' && todayRes.value.ok) {
        const json = await todayRes.value.json();
        if (json.success) todayGames = json.data || [];
      }

      if (liveRes.status === 'fulfilled' && liveRes.value.ok) {
        const json = await liveRes.value.json();
        if (json.success) liveGames = json.data || [];
      }

      if (statusRes.status === 'fulfilled' && statusRes.value.ok) {
        const json = await statusRes.value.json();
        if (json.success && json.data) {
          setApiStatus({
            plan: json.data.subscription?.plan,
            requestsUsed: json.data.requests?.current,
            requestsLimit: json.data.requests?.limit_day,
          });
        }
      }

      const totalRecords = todayGames.length + liveGames.length;

      // Update API-Hockey source status
      setSources((prev) =>
        prev.map((s) => {
          if (s.id === 'api_hockey') {
            return {
              ...s,
              status: 'healthy',
              latency,
              lastSync: `${timeSince(startTime)} ago`,
              records: totalRecords,
            };
          }
          return s;
        })
      );

      // Build recent runs
      const runs: PipelineRun[] = [];
      if (liveGames.length > 0) {
        runs.push({
          source: 'API-Hockey',
          type: 'live',
          status: 'success',
          records: liveGames.length,
          duration: `${latency}ms`,
          anomalies: 0,
          time: 'just now',
        });
      }
      if (todayGames.length > 0) {
        runs.push({
          source: 'API-Hockey',
          type: 'scheduled',
          status: 'success',
          records: todayGames.length,
          duration: `${latency}ms`,
          anomalies: 0,
          time: 'just now',
        });
      }
      setRecentRuns(runs);

      // Build history point
      const hour = new Date().getHours();
      setHistoryData((prev) => {
        const newPoint = { time: `${hour}:00`, records: totalRecords, errors: 0, latency };
        const updated = [...prev.slice(-6), newPoint];
        return updated;
      });

      setLastRefresh(Date.now());
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Fetch error';
      setError(msg);
      setSources((prev) =>
        prev.map((s) =>
          s.id === 'api_hockey' ? { ...s, status: 'degraded' } : s
        )
      );
    }
  }, []);

  useEffect(() => {
    fetchLiveData();
    const interval = setInterval(fetchLiveData, 30000);
    return () => clearInterval(interval);
  }, [fetchLiveData]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchLiveData();
    setRefreshing(false);
  };

  const totalRecords = sources.reduce((a, s) => a + s.records, 0);
  const avgUptime = (sources.reduce((a, s) => a + s.uptime, 0) / sources.length).toFixed(1);

  return (
    <div className="space-y-6 fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-foreground">Real-Time Data Pipeline</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Production ingestion · 5 sources actives · API-Hockey LIVE
            {apiStatus && (
              <span className="ml-2 text-primary font-mono-data">
                [{apiStatus.requestsUsed}/{apiStatus.requestsLimit} req/day · {apiStatus.plan}]
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {error && (
            <div className="flex items-center gap-2 px-3 py-1.5 rounded bg-negative-subtle border border-negative/20">
              <span className="text-xs text-negative font-mono-data truncate max-w-[200px]">{error}</span>
            </div>
          )}
          <div className="flex items-center gap-2 px-3 py-1.5 rounded bg-positive-subtle border border-positive/20">
            <span className="w-2 h-2 rounded-full bg-positive pulse-dot" />
            <span className="text-xs font-medium text-positive font-mono-data">PIPELINE ACTIF</span>
          </div>
          <button onClick={handleRefresh} className="btn-ghost flex items-center gap-2 text-sm" disabled={refreshing}>
            <Icon name="ArrowPathIcon" size={16} className={refreshing ? 'animate-spin text-primary' : 'text-muted-foreground'} />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Sources Actives', value: `${sources.filter((s) => s.status === 'healthy').length}/5`, sub: 'Toutes opérationnelles', icon: 'ServerIcon', color: 'text-positive' },
          { label: 'Records Ingérés', value: totalRecords > 0 ? totalRecords.toLocaleString() : '—', sub: 'Matchs NHL aujourd\'hui', icon: 'CircleStackIcon', color: 'text-primary' },
          { label: 'Uptime Moyen', value: `${avgUptime}%`, sub: '30 derniers jours', icon: 'SignalIcon', color: 'text-positive' },
          { label: 'Dernière Sync', value: timeSince(lastRefresh), sub: 'API-Hockey · Auto 30s', icon: 'ClockIcon', color: 'text-warning' },
        ].map((kpi) => (
          <div key={kpi.label} className="card p-4">
            <div className="flex items-center gap-2 mb-2">
              <Icon name={kpi.icon as Parameters<typeof Icon>[0]['name']} size={16} className={kpi.color} />
              <span className="text-xs text-muted-foreground">{kpi.label}</span>
            </div>
            <div className={`text-2xl font-bold font-mono-data ${kpi.color}`}>{kpi.value}</div>
            <div className="text-xs text-muted-foreground mt-1">{kpi.sub}</div>
          </div>
        ))}
      </div>

      {/* Sources Grid */}
      <div>
        <h2 className="text-sm font-semibold text-foreground mb-3">Sources de Données</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
          {sources.map((source) => (
            <button
              key={source.id}
              onClick={() => setActiveSource(source.id)}
              className={`card p-4 text-left transition-all ${activeSource === source.id ? 'border-primary/50 bg-primary/5' : 'hover:border-border/80'}`}
            >
              <div className="flex items-center justify-between mb-3">
                <span className="text-xl">{source.icon}</span>
                <span className="flex items-center gap-1">
                  <span className={`w-1.5 h-1.5 rounded-full ${source.status === 'healthy' ? 'bg-positive' : source.status === 'degraded' ? 'bg-warning' : 'bg-negative'}`} />
                  <span className={`text-2xs font-mono-data ${source.status === 'healthy' ? 'text-positive' : source.status === 'degraded' ? 'text-warning' : 'text-negative'}`}>
                    {source.id === 'api_hockey' ? 'LIVE' : source.status.toUpperCase()}
                  </span>
                </span>
              </div>
              <div className="text-xs font-semibold text-foreground mb-1">{source.name}</div>
              <div className="space-y-1">
                <div className="flex justify-between text-2xs">
                  <span className="text-muted-foreground">Uptime</span>
                  <span className="font-mono-data text-positive">{source.uptime}%</span>
                </div>
                <div className="flex justify-between text-2xs">
                  <span className="text-muted-foreground">Latence</span>
                  <span className="font-mono-data text-foreground">{source.latency}ms</span>
                </div>
                <div className="flex justify-between text-2xs">
                  <span className="text-muted-foreground">Dernier sync</span>
                  <span className="font-mono-data text-primary">{source.lastSync}</span>
                </div>
                <div className="flex justify-between text-2xs">
                  <span className="text-muted-foreground">Records</span>
                  <span className="font-mono-data text-foreground">{source.records > 0 ? source.records : '—'}</span>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Pipeline History */}
        <div className="card p-4">
          <h3 className="text-sm font-semibold text-foreground mb-4">Volume d&apos;Ingestion (session)</h3>
          {historyData.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={historyData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1E2D4A" />
                <XAxis dataKey="time" tick={{ fill: '#6B7A99', fontSize: 10 }} />
                <YAxis tick={{ fill: '#6B7A99', fontSize: 10 }} />
                <Tooltip contentStyle={TOOLTIP_STYLE} />
                <Bar dataKey="records" fill="#00D4FF" radius={[2, 2, 0, 0]} name="Records" />
                <Bar dataKey="errors" fill="#FF4D4D" radius={[2, 2, 0, 0]} name="Erreurs" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-[200px] text-muted-foreground text-sm">
              <Icon name="ArrowPathIcon" size={16} className="animate-spin mr-2" />
              Chargement des données live...
            </div>
          )}
        </div>

        {/* Data Quality */}
        <div className="card p-4">
          <h3 className="text-sm font-semibold text-foreground mb-4">Qualité des Données par Source</h3>
          <div className="space-y-3">
            {QUALITY_DATA.map((q) => (
              <div key={q.source}>
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-muted-foreground">{q.source}</span>
                  <span className="font-mono-data text-foreground">{((q.completeness + q.accuracy + q.freshness + q.consistency) / 4).toFixed(1)}%</span>
                </div>
                <div className="h-1.5 bg-card-hover rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-primary to-positive"
                    style={{ width: `${(q.completeness + q.accuracy + q.freshness + q.consistency) / 4}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Recent Runs */}
      <div className="card p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-foreground">Exécutions Récentes du Pipeline</h3>
          <span className="text-xs text-muted-foreground font-mono-data">
            {recentRuns.length > 0 ? `${recentRuns.length} run(s) · API-Hockey LIVE` : 'En attente de données...'}
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-border">
                {['Source', 'Type', 'Statut', 'Records', 'Durée', 'Anomalies', 'Temps'].map((h) => (
                  <th key={h} className="text-left py-2 px-3 text-muted-foreground font-medium">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {recentRuns.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-6 text-center text-muted-foreground">
                    <Icon name="ArrowPathIcon" size={14} className="inline animate-spin mr-2" />
                    Connexion à API-Hockey en cours...
                  </td>
                </tr>
              ) : (
                recentRuns.map((run, i) => (
                  <tr key={i} className="border-b border-border/50 hover:bg-card-hover transition-colors">
                    <td className="py-2 px-3 font-medium text-foreground">{run.source}</td>
                    <td className="py-2 px-3">
                      <span className="status-badge bg-info-subtle text-primary">{run.type}</span>
                    </td>
                    <td className="py-2 px-3">
                      <span className="status-badge bg-positive-subtle text-positive">✓ {run.status}</span>
                    </td>
                    <td className="py-2 px-3 font-mono-data text-foreground">{run.records.toLocaleString()}</td>
                    <td className="py-2 px-3 font-mono-data text-muted-foreground">{run.duration}</td>
                    <td className="py-2 px-3">
                      <span className={run.anomalies > 0 ? 'text-warning font-mono-data' : 'text-muted-foreground font-mono-data'}>{run.anomalies}</span>
                    </td>
                    <td className="py-2 px-3 text-muted-foreground">{run.time}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Failover Config */}
      <div className="card p-4">
        <h3 className="text-sm font-semibold text-foreground mb-4">Configuration Failover & Priorité</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { priority: 1, source: 'NHL Official API', fallback: 'API-Hockey', status: 'primary', retries: 3, timeout: '5s' },
            { priority: 2, source: 'API-Hockey', fallback: 'Manual Cache', status: 'secondary', retries: 3, timeout: '10s' },
            { priority: 3, source: 'The Odds API', fallback: 'Cached Odds', status: 'tertiary', retries: 2, timeout: '15s' },
          ].map((config) => (
            <div key={config.priority} className="p-3 rounded-lg bg-card-hover border border-border/50">
              <div className="flex items-center gap-2 mb-2">
                <span className="w-5 h-5 rounded-full bg-primary/20 text-primary text-xs flex items-center justify-center font-bold">{config.priority}</span>
                <span className="text-xs font-semibold text-foreground">{config.source}</span>
              </div>
              <div className="space-y-1 text-2xs">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Fallback</span>
                  <span className="text-foreground">{config.fallback}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Retries</span>
                  <span className="font-mono-data text-foreground">{config.retries}x</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Timeout</span>
                  <span className="font-mono-data text-foreground">{config.timeout}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
